import React from "react";
import { nanoid } from "nanoid";
import { useSelector } from "react-redux";

import { cartSelector } from "../../redux/cart/selectors";
import { setCartItems } from "../../redux/cart/slice";
import { useAppDispatch } from "../../redux/store";
import { subscriptionsSelector } from "../../redux/subscriptions/selectors";
import { rerenderSelector } from "../../redux/rerender/selectors";
import { setSelectedHalls } from "../../redux/halls/slice";
import { partnerIdSelector } from "../../redux/user/selectors";
import { cancelReserveArena } from "../../redux/rent/bitrixArenas";
import { fetchNotices } from "../../redux/notices/asyncActions";

import { Button, Header, LoginModal, Modal, Title, Breadcrumbs } from "../../components";
import { CartItem, AcceptTermsForm, EmptyCart } from "./components";

import {
  addCurrencyWord,
  setTotalPriceToStorage,
} from "../../utils";
import { ModalTexts } from "../../resources/locale";

import styles from "./Cart.module.scss";

import { CartPropsType, ITotalCartItems } from "./types";
import { ISelectedSubscr } from "../../redux/subscriptions/types";
import { ISelectedHall } from "../../redux/halls/types";
import { useLocation } from "react-router-dom";

export const Cart: React.FC<CartPropsType> = ({ title }) => {
  const { totalCartItems } = useSelector(cartSelector);
  const { selectedSubscr } = useSelector(subscriptionsSelector);
  const { rerender } = useSelector(rerenderSelector);
  const partnerId = useSelector(partnerIdSelector);
  const [isOpenModalNoAdultSbscr, setIsOpenModalNoAdultSbscr] = React.useState(false);
  const [isOpenModalNoAuth, setIsOpenModalNoAuth] = React.useState(false);
  const dispatch = useAppDispatch();

  const location = useLocation();

  const removedDueToExpiryRef = React.useRef<Set<string>>(new Set());

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Перед оплатой обновляем объявления, чтобы галочки были актуальными
  React.useEffect(() => {
    dispatch(fetchNotices());
  }, [dispatch]);

  const prevSelectedSubscrRef = React.useRef(selectedSubscr);
  
  React.useEffect(() => {
    const currentSubscrStr = JSON.stringify(selectedSubscr);
    const prevSubscrStr = JSON.stringify(prevSelectedSubscrRef.current);
    
    if (currentSubscrStr !== prevSubscrStr) {
      setTotalCartItems();
      prevSelectedSubscrRef.current = selectedSubscr;
    }
  }, [selectedSubscr]);

  React.useEffect(() => {}, [rerender]);

  React.useEffect(() => {
    const EXPIRY_MS = 10 * 60 * 1000;

    const checkAndCleanupExpiredHalls = async () => {
      const now = Date.now();
      const seats = totalCartItems.seats || {};
      let hasExpired = false;
      const newSeats = { ...seats };

      for (const key in seats) {
        const hall = seats[key] as ISelectedHall;
        
        if (hall.timestamp && (now - hall.timestamp) > EXPIRY_MS) {
          if (!removedDueToExpiryRef.current.has(key)) {
            removedDueToExpiryRef.current.add(key);
            hasExpired = true;

            if (hall.courtId && hall.startTime && hall.finishTime && partnerId) {
              try {
                await cancelReserveArena({
                  partnerId: String(partnerId),
                  newReserve: [],
                  cancelReserve: [{
                    courtId: hall.courtId,
                    startTime: hall.startTime,
                    finishTime: hall.finishTime,
                  }],
                });
              } catch (error) {
              }
            }

            delete newSeats[key];
          }
        }
      }

      if (hasExpired) {
        const updatedCartItems = {
          seats: newSeats,
          subscriptions: { ...totalCartItems.subscriptions },
        };
        dispatch(setCartItems(updatedCartItems));
        dispatch(setSelectedHalls(newSeats as { [key: string]: ISelectedHall }));
      }
    };

    checkAndCleanupExpiredHalls();

    const interval = setInterval(checkAndCleanupExpiredHalls, 60000);

    return () => clearInterval(interval);
  }, [totalCartItems, partnerId, dispatch]);

  const cartItems = renderCartItems(totalCartItems);
  const totalPrice = getTotalPrice(totalCartItems);
  const isCartEmpty = testCartEmpty(totalCartItems);

  const setTotalCartItems = () => {
    const newTotalCartItems = {
      seats: { ...totalCartItems.seats },
      subscriptions: { ...selectedSubscr },
    };

    dispatch(setCartItems(newTotalCartItems));
  };

  function getSortedCartItems(cartItems: ITotalCartItems): (ISelectedSubscr | ISelectedHall)[] {
    const items: (ISelectedSubscr | ISelectedHall)[] = [];
    
    if (cartItems.subscriptions && Object.keys(cartItems.subscriptions).length > 0) {
      const subscriptions = Object.values(cartItems.subscriptions) as ISelectedSubscr[];
      
      subscriptions.sort((a: any, b: any) => {
        const typeA = a.subscription?.type || '';
        const typeB = b.subscription?.type || '';
        
        if (typeA !== typeB) {
          return typeA.localeCompare(typeB);
        }
        
        const addedAtA = a.addedAt || 0;
        const addedAtB = b.addedAt || 0;
        return addedAtA - addedAtB;
      });
      
      items.push(...subscriptions);
    }
    
    if (cartItems.seats && Object.keys(cartItems.seats).length > 0) {
      const halls = Object.values(cartItems.seats) as ISelectedHall[];
      
      halls.sort((a, b) => {
        if (a.date !== b.date) {
          return new Date(a.date).getTime() - new Date(b.date).getTime();
        }
        
        return a.time.localeCompare(b.time);
      });
      
      items.push(...halls);
    }
    
    return items;
  }

  function renderCartItems(cartItems: ITotalCartItems) {
    const sortedItems = getSortedCartItems(cartItems);

    return sortedItems.map((currentItem: ISelectedSubscr | ISelectedHall | any) => {
      let currentPrice: number = 0;
      let itemType: string = '';
      let itemId: number = 0;

      if ('subscription' in currentItem) {
        itemType = 'subscriptions';
        itemId = currentItem.subscription.id;
        
        if (currentItem.subscription?.type === "family") {
          currentPrice = currentItem.subscription.price;
        } else {
          currentPrice = currentItem.quantity
            ? currentItem.subscription.price * currentItem.quantity
            : currentItem.subscription.price;
        }
      } else {
        itemType = 'seats';
        itemId = currentItem.hallId;
        currentPrice = currentItem.price;
      }

      const currentKey = nanoid();

      let expirationDays = currentItem?.subscription?.expiration_days;
      if (!expirationDays && itemType === 'subscriptions') {
        const title = currentItem?.subscription?.title?.toLowerCase() || '';
        if (title.includes('разовое') || title.includes('разовая') || title.includes('1 день')) {
          expirationDays = 1;
        } else if (title.includes('3 месяц') || title.includes('90')) {
          expirationDays = 90;
        } else if (title.includes('1 месяц') || title.includes('30')) {
          expirationDays = 30;
        }
      }

      return (
        <CartItem
          key={currentKey}
          itemId={itemId}
          itemType={itemType}
          date={currentItem?.date}
          time={currentItem?.time}
          quantity={currentItem?.quantity}
          expiration={currentItem?.subscription?.cart_expiration_text}
          expirationDays={expirationDays}
          price={currentPrice}
          title={
            itemType === "seats"
              ? currentItem.hallTitle
              : currentItem.subscription?.title
          }
          setIsOpenModalNoAdultSbscr={setIsOpenModalNoAdultSbscr}
        />
      );
    });
  }

  function getTotalPrice(cartItems: ITotalCartItems | any) {
    let totalPrice = 0;
    for (let field in cartItems) {
      for (let key in cartItems[field]) {
        if (cartItems[field][key].subscription?.type === "family") {
          totalPrice += cartItems[field][key].subscription.price
        } else {
          totalPrice += cartItems[field][key].subscription
            ? cartItems[field][key].subscription.price * cartItems[field][key].quantity
            : cartItems[field][key].price;
        }
      }
    }

    setTotalPriceToStorage(totalPrice);
    return addCurrencyWord(totalPrice);
  }

  function testCartEmpty(cartItems: ITotalCartItems) {
    return !!(
      Object.keys(cartItems.subscriptions || {}).length ||
      Object.keys(cartItems.seats || {}).length
    );
  }

  const handleCloseModalNoAdultSbscr = () => setIsOpenModalNoAdultSbscr(false);

  return (
    <>
      <Header title={title} />
      <main>
        <div className="container">
          <Breadcrumbs />
        </div>
        <section className="container">
          <Title>{title}</Title>

          {isCartEmpty ? (
            <div className={styles.cartContainer}>
              <div className={styles.cartItems}>{cartItems}</div>

              <div className={styles.cartSummary}>
                <div className={styles.totalSum}>
                  <span className={styles.totalSum_text}>Общая сумма</span>
                  <span className={styles.totalSum_price}>{totalPrice}</span>
                </div>
                <AcceptTermsForm
                  totalPrice={totalPrice}
                  setIsOpenModalNoAuth={setIsOpenModalNoAuth}
                />
              </div>
            </div>
          ) : (
            <EmptyCart />
          )}
        </section>
      </main>

      {isOpenModalNoAuth ? (
        <LoginModal isOpen={isOpenModalNoAuth} setIsOpen={setIsOpenModalNoAuth} />
      ) : null}

      {isOpenModalNoAdultSbscr ? (
        <Modal
          title={ModalTexts.cart.noAdultsSubscr.TITLE}
          subTitle={ModalTexts.cart.noAdultsSubscr.SUB_TITLE}
          isOpen={isOpenModalNoAdultSbscr}
          setIsOpen={setIsOpenModalNoAdultSbscr}
        >
          <Button newStyles={styles.button} onClickFunc={handleCloseModalNoAdultSbscr}>
            Ок
          </Button>
        </Modal>
      ) : null}
    </>
  );
};
